import { Css, Palette, Tooltip, useTestIds } from "@homebound/beam";
import { ScatterPlotRawSerie } from "@nivo/scatterplot";
import { differenceInCalendarDays, startOfToday } from "date-fns";
import { useMemo } from "react";
import { Icon } from "src/components";
import { ScatterPlotChart } from "src/components/charts/ScatterPlotChart";
import {
  BudgetOverviewTab_ProjectFragment,
  BudgetOverviewTab_SellableProjectFragment,
} from "src/routes/projects/budget/components/reports/BudgetOverviewTab.generated";
import { formatCentsToPrice } from "src/utils";
import { DateOnly } from "src/utils/dates";

type AverageVarianceWidgetWidgetProps = {
  project: BudgetOverviewTab_ProjectFragment;
};

export function VerticalCompleteBudgetStatusWidget({ project }: AverageVarianceWidgetWidgetProps) {
  const tid = useTestIds({});
  const { chartData, totalRemainingBudgetInCents } = useMemo(
    () => mapToScatterPlotChartData(project.sellableProjects),
    [project],
  );
  // Calculate X/Y axis ticks based on the chart data
  const { yTicks, xTicks } = useMemo(() => getChartTicks(chartData.flatMap((p) => p.data)), [chartData]);

  return (
    <div css={Css.w100.mw0.hPx(320).p3.pb0.bgWhite.ba.bcGray300.bw2.br8.$}>
      <div {...tid.totalLabel} css={Css.tal.xl2.pb1.$}>
        {formatCentsToPrice(totalRemainingBudgetInCents)}
      </div>
      <div css={Css.lg.gray700.pb3.df.$}>
        Vertical Complete Budget Status
        <div css={Css.mya.pl1.$}>
          <Tooltip
            title={`The total budget to review for completed projects in this ${project.__typename?.toLowerCase()}.`}
            placement="top"
          >
            <Icon icon="infoCircle" color={Palette.Gray600} inc={2.3} />
          </Tooltip>
        </div>
      </div>
      <div css={Css.h("60%").w100.$} {...tid.ScatterPlotChart}>
        <ScatterPlotChart
          dataset={chartData.isEmpty ? emptyDataset : chartData}
          colors={[Palette.Green500, Palette.Yellow500, Palette.Red500]}
          loadingColor={[Palette.Transparent]}
          emptyStateTooltipText={chartData.isEmpty ? "There are no projects past Vertical Complete yet." : undefined}
          tooltipValueFormat={({ node }) =>
            `${node.data.id} is ${node.data.x} days after it's Vertical Complete Date, and has ${formatCentsToPrice(node.data.y as number)} budget remaining.`
          }
          margin={{ top: 5, bottom: 47, left: 75, right: chartData.isEmpty ? 6 : 10 }}
          disabled={chartData.isEmpty}
          gridYValues={yTicks}
          gridXValues={xTicks}
          axisLeft={{
            tickValues: yTicks,
            tickPadding: 10,
            tickSize: 0,
            legend: "Budget Remaining",
            legendPosition: "middle",
            legendOffset: -70,
            format: (value) => formatCentsToPrice(value, { trim: true, rounded: true }),
          }}
          axisBottom={{
            tickValues: xTicks,
            tickPadding: 10,
            tickSize: 0,
            legend: "Days Since Vertical Complete",
            legendPosition: "middle",
            legendOffset: 40,
            format: (value) => Math.round(value),
          }}
          xScale={{ type: "linear", min: "auto", max: "auto" }}
          yScale={{ type: "linear", min: "auto", max: "auto" }}
        />
      </div>
    </div>
  );
}

export function mapToScatterPlotChartData(data: BudgetOverviewTab_SellableProjectFragment[]): {
  chartData: ScatterPlotRawSerie<{ x: number; y: number; id: string }>[];
  totalRemainingBudgetInCents: number;
} {
  const today = new DateOnly(startOfToday());
  // Filter out project that are not vertical complete
  const filteredData = data
    .filter((p) => !!p.verticalCompleteDate && p.uncommittedCostInCents > 0)
    .map((p) => ({ ...p, daysPastVerticalComplete: differenceInCalendarDays(today, p.verticalCompleteDate!) }))
    .sortBy((p) => p.daysPastVerticalComplete)
    .reverse();
  if (filteredData.isEmpty) return { chartData: [], totalRemainingBudgetInCents: 0 };
  // Map to chart data, grouping by days past vertical complete
  const points = filteredData.map((p) => ({ x: p.daysPastVerticalComplete, y: p.uncommittedCostInCents, id: p.name }));
  const chartData = Array.from(
    points.groupBy((point) => daysPastVerticalComplete.find((t) => point.x <= t) ?? "").toEntries(),
    ([id, data]) => ({ id, data }),
  );
  return { chartData, totalRemainingBudgetInCents: filteredData.sum((p) => p.uncommittedCostInCents) };
}

function getChartTicks(data: { x: number; y: number }[]): {
  xTicks: number[];
  yTicks: number[];
} {
  if (data.isEmpty) return { xTicks: xTicksDefault, yTicks: yTicksDefault };

  const tickValues = (values: number[], steps: number) => {
    const min = Math.min(...values);
    const max = Math.max(...values);
    return min === max ? [min] : Array.from({ length: steps }, (_, i) => min + ((max - min) / (steps - 1)) * i);
  };

  const xValues = data.map((d) => d.x);
  const yValues = data.map((d) => d.y);
  return { xTicks: tickValues(xValues, 7), yTicks: tickValues(yValues, 5) };
}

const yTicksDefault = [0, 12500_00, 25000_00, 37500_00, 50000_00];
const xTicksDefault = [0, 10, 20, 30, 40, 50, 60];
const emptyDataset = [
  {
    id: "empty_dataset",
    data: [
      { x: 0, y: 0 },
      { x: 60, y: 50000_00 }, // Use full range of x/y axis
    ],
  },
];

// Thresholds used to determine datapoint colors on the chart
const daysPastVerticalComplete = [30, 45];
